import { SessionBooking, UserRole } from "@joonapp/web-shared"
import { GroupChannel } from "@sendbird/chat/groupChannel"
import dayjs from "dayjs"
import { useMemo } from "react"

import useCoachSelectStore from "./useCoachSelectStore"
import { useSendbird } from "../../../components/sendbird/SBProvider"
import { useCoachingGroupsQuery } from "../../../networking/coachingGroups/queries"
import { useUpcomingSessionsQuery } from "../../../networking/sessions/queries"
import { CoachingGroup } from "../../../types"
import { isInactiveClient } from "../../../util/coachingGroups"
import { isReactivatedClient } from "../../../util/coachingGroups"
import { isNewClient } from "../../../util/coachingGroups"

const useCoachingGroups = () => {
  const { data: coachingGroups } = useCoachingGroupsQuery()
  const { data: upcomingSessions } = useUpcomingSessionsQuery()
  const { getChannelByUrl, channels, messages } = useSendbird()
  const { selectedCoachUserId } = useCoachSelectStore()

  const coachingGroupsWithUpcomingSessions = useMemo(() => {
    const filteredGroups = coachingGroups?.filter((group) => {
      const coachProfile = group.profiles.find(
        (profile) => profile.role === UserRole.COACH
      )
      if (selectedCoachUserId) {
        return coachProfile?.user.id === selectedCoachUserId
      }
      return true
    })

    const coachingGroupsWithUpcomingSessions = filteredGroups?.map(
      (coachingGroup) => {
        const upcomingSession = upcomingSessions?.next_sessions.find(
          (session) => session.group_id === coachingGroup.id
        )
        const channelUrl = coachingGroup.sendbird_channel_url
        const channel = getChannelByUrl(channelUrl)
        return { coachingGroup, upcomingSession, channel }
      }
    )

    return coachingGroupsWithUpcomingSessions
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [coachingGroups, upcomingSessions, selectedCoachUserId])

  const sortedCoachingGroups = coachingGroupsWithUpcomingSessions?.map(
    ({ coachingGroup }) => coachingGroup
  )

  // Split into upcoming and remaining channel sections
  const { upcomingSessionChannels, remainingChannels } = useMemo(() => {
    const upcomingSessionChannels = [] as {
      coachingGroup: CoachingGroup
      upcomingSession: SessionBooking | undefined
      channel: GroupChannel | null
    }[]
    const remainingChannels = [] as {
      coachingGroup: CoachingGroup
      upcomingSession: SessionBooking | undefined
      channel: GroupChannel | null
    }[]

    coachingGroupsWithUpcomingSessions?.forEach((group) => {
      const isUpcomingSession =
        group.upcomingSession?.end_datetime &&
        isUpcoming(group.upcomingSession.end_datetime)

      if (isUpcomingSession) upcomingSessionChannels.push(group)
      else remainingChannels.push(group)
    })

    const sortedUpcomingSessionChannels = sortUpcomingSessionChannels(
      upcomingSessionChannels
    )
    // All other clients are sorted by:
    // First New clients,
    // Then Reactivated clients
    // Then all other active clients
    // and then Inactive clients
    // ^^ each of these subsections is sorted by last message time
    const sortedRemainingChannels = sortRemainingChannels(remainingChannels)

    return {
      upcomingSessionChannels: sortedUpcomingSessionChannels,
      remainingChannels: sortedRemainingChannels,
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [coachingGroupsWithUpcomingSessions, channels, messages])

  return {
    coachingGroupsWithUpcomingSessions,
    upcomingSessionChannels,
    remainingChannels,
    sortedCoachingGroups,
  }
}

export default useCoachingGroups

const sortUpcomingSessionChannels = (
  channels: {
    coachingGroup: CoachingGroup
    upcomingSession: SessionBooking | undefined
    channel: GroupChannel | null
  }[]
) => {
  return channels.sort((a, b) => {
    const aSessionDate = a.upcomingSession?.start_datetime
      ? dayjs(a.upcomingSession.start_datetime).valueOf()
      : 0
    const bSessionDate = b.upcomingSession?.start_datetime
      ? dayjs(b.upcomingSession.start_datetime).valueOf()
      : 0
    return aSessionDate - bSessionDate
  })
}

const sortRemainingChannels = (
  channels: {
    coachingGroup: CoachingGroup
    upcomingSession: SessionBooking | undefined
    channel: GroupChannel | null
  }[]
) => {
  return channels.sort((a, b) => {
    const aIsNewClient = isNewClient(a.coachingGroup.date_created)
    const bIsNewClient = isNewClient(b.coachingGroup.date_created)

    if (aIsNewClient && !bIsNewClient) return -1
    if (!aIsNewClient && bIsNewClient) return 1

    const aIsReactivated = isReactivatedClient(a.coachingGroup)
    const bIsReactivated = isReactivatedClient(b.coachingGroup)

    if (aIsReactivated && !bIsReactivated) return -1
    if (!aIsReactivated && bIsReactivated) return 1

    const aIsInactive = isInactiveClient(a.coachingGroup)
    const bIsInactive = isInactiveClient(b.coachingGroup)

    if (aIsInactive && !bIsInactive) return 1
    if (!aIsInactive && bIsInactive) return -1

    return getLastMessageTime(b.channel) - getLastMessageTime(a.channel)
  })
}

const isUpcoming = (dateStr: string) => {
  const now = dayjs()
  const tomorrow = now.add(1, "day")

  const sessionDate = dayjs(dateStr)
  const isToday = sessionDate.isSame(now, "day")
  const isTomorrow = sessionDate.isSame(tomorrow, "day")

  // include today's future sessions and all of tomorrow's sessions
  if (isToday) return sessionDate.isAfter(now)
  return isTomorrow
}

const getLastMessageTime = (channel: any) => {
  return channel?.lastMessage?.createdAt ?? 0
}
